import { useLayoutEffect, useRef } from 'react';
import Markdown from 'react-markdown';
import { ScrollArea } from '~/primitives/scroll-area';
import { cn, fetchPublicAsset, seo, toCamelCase } from '~/util';
import { Circle } from 'lucide-react';
import { createFileRoute, Link, useLocation } from '@tanstack/react-router';
import { ListTabSwitch } from '~/partials/TabSwitch';

// Guidelines folder should clone to ~/public/guideline_files via `yarn build-guidelines`

export const guidelinesPath = (list: 'classic' | 'platformer') =>
    `/guideline_files/${toCamelCase(list)}/`;

export interface GuidelineFolder {
    name: string;
    contents: string[];
}

const getIdFromHeading = (heading: string): string[] => {
    const header = heading?.split('{')[0]?.trim();
    const meta = heading?.split('{')[1]?.split('}')[0];
    const id = meta?.split(':')[1];
    return [header, id];
};

const getHeadingsInFile = (file: string): string[] | undefined => {
    const res = file.match(/^(#+) (.+)$/gm);
    if (!res) return undefined;
    return res;
};

export const Route = createFileRoute('/guidelines')({
    staleTime: 0,
    loaderDeps: ({ search: { list } }) => ({ list }),
    loader: async ({ deps: { list } }) => {
        const response = await fetchPublicAsset(
            `${guidelinesPath(list)}data.json`,
        );
        if (!response.ok) {
            console.error('Failed to fetch markdown files:', response);
            throw new Error(
                `Failed to fetch markdown files: ${response.statusText}`,
            );
        }
        return (await response.json()) as string[];
    },
    head: () => ({
        meta: seo({
            title: 'Guidelines',
            description:
                'Detailed guidelines for submitting completions to the AREDL.',
        }),
    }),
    component: Guidelines,
});

export const GuidelineLink = ({
    link,
    text,
    className,
}: {
    link: string;
    text: React.ReactNode;
    className?: string;
}) => (
    <Link
        to={link?.startsWith('#') ? '/guidelines' : link}
        hash={link?.startsWith('#') ? link.slice(1) : undefined}
        target={link?.startsWith('#') ? undefined : '_blank'}
        rel="noreferrer"
        className={cn(
            'font-bold underline text-gray-400 hover:text-primary transition-colors duration-200',
            className,
        )}
    >
        {text}
    </Link>
);

function Guidelines() {
    const markdownContent = Route.useLoaderData();
    const scrollAreaRef = useRef<HTMLDivElement>(null);
    const didInitialScroll = useRef(false);

    const hash = useLocation({ select: ({ hash }) => hash });

    useLayoutEffect(() => {
        if (didInitialScroll.current) return;
        if (!markdownContent || markdownContent.length === 0) return;
        if (typeof window === 'undefined') return;
        if (!hash) return;

        const id = hash.startsWith('#') ? hash.slice(1) : hash;
        const element = document.getElementById(id);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            didInitialScroll.current = true;
        }
    }, [markdownContent, hash]);

    return (
        <div className="flex flex-col lg:flex-row gap-9 px-[6vw]">
            <div className="lg:w-3/12 lg:h-[calc(100vh-150px)] bg-background-dark/80 backdrop-blur-md rounded-xl shadow-lg border border-gray-800">
                <ScrollArea className="h-full px-4 py-5">
                    <ListTabSwitch from="/guidelines" className="mb-4" />
                    <div className="mb-6">
                        {getHeadingsInFile(markdownContent.join(''))?.map(
                            (heading) => {
                                const level = heading.match(/^#+/)?.[0].length;
                                const [header, id] = getIdFromHeading(heading);
                                if (level === 1) {
                                    return (
                                        <div
                                            key={heading}
                                            className="flex items-center gap-x-2 mb-3"
                                        >
                                            <Link
                                                to="/guidelines"
                                                hash={id}
                                                className="font-bold underline text-white tracking-wide hover:text-primary transition-colors duration-200"
                                            >
                                                {header.replace(/^#+ /, '')}
                                            </Link>
                                        </div>
                                    );
                                } else if (level === 2) {
                                    return (
                                        <div
                                            key={heading}
                                            className="flex items-center gap-x-2 ml-5 mb-2"
                                        >
                                            <Circle
                                                fill="#ffffff79"
                                                stroke="#ffffff00"
                                                height={8}
                                                width={8}
                                                className="my-auto"
                                            />
                                            <Link
                                                to="/guidelines"
                                                hash={id}
                                                className="font-bold underline text-gray-400 hover:text-primary transition-colors duration-200"
                                            >
                                                {header.replace(/^#+ /, '')}
                                            </Link>
                                        </div>
                                    );
                                }
                            },
                        )}
                    </div>
                </ScrollArea>
            </div>
            <div className="w-full lg:w-9/12 lg:h-[calc(100vh-150px)] rounded-md shadow-lg overflow-hidden">
                <ScrollArea ref={scrollAreaRef} className="h-full">
                    {markdownContent.map((file) => (
                        <div
                            key={file}
                            className="bg-background-dark/80 backdrop-blur-md rounded-xl p-5 px-[5vw] mb-6 shadow-md border border-gray-800 w-full"
                        >
                            <Markdown
                                components={{
                                    a: ({ href, children }) => (
                                        <GuidelineLink
                                            link={href ?? '#'}
                                            text={children}
                                        />
                                    ),
                                    ul: ({ children }) => (
                                        <ul className="list-disc space-y-2 text-left py-2 pl-6 marker:text-primary">
                                            {children}
                                        </ul>
                                    ),
                                    h1: ({ children }) => {
                                        const text = children?.toString();
                                        if (!text) return null;
                                        const [header, id] =
                                            getIdFromHeading(text);
                                        return (
                                            <h1
                                                id={id}
                                                className="text-2xl font-bold py-4 text-center tracking-wide border-b border-gray-700"
                                            >
                                                {header}
                                            </h1>
                                        );
                                    },
                                    h2: ({ children }) => {
                                        const text = children?.toString();
                                        if (!text) return null;
                                        const [header, id] =
                                            getIdFromHeading(text);
                                        return (
                                            <h2
                                                id={id}
                                                className="text-xl font-semibold py-5 text-center text-gray-200 tracking-wide"
                                            >
                                                {header}
                                            </h2>
                                        );
                                    },
                                    h3: ({ children }) => (
                                        <h3 className="font-semibold text-[1.3rem] py-3 text-center text-gray-300">
                                            {children}
                                        </h3>
                                    ),
                                    p: ({ children }) => (
                                        <p className="leading-relaxed text-gray-300 py-2">
                                            {children}
                                        </p>
                                    ),
                                    code: ({ children }) => (
                                        <code className="bg-stone-800/60 rounded-sm px-1 font-mono text-gray-400">
                                            {children}
                                        </code>
                                    ),
                                }}
                            >
                                {file}
                            </Markdown>
                        </div>
                    ))}
                </ScrollArea>
            </div>
        </div>
    );
}
